"use client";

import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useLazyQuery } from "@apollo/client";
import { SITE_CREATION_HOSTS } from "@constants/global";
import { ESystemRoles } from "@constants/SystemRoles";
import { APP_VERSION } from "components/Roadmap/ItemPathNode/constants";
import UserAvatar from "components/User/UserAvatar";
import {
  CommandPaletteV2,
  type CommandPaletteV2Section
} from "components/ui/v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlUser } from "gql";
import { IVendorUserListResponse } from "gql/User/queries";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PRIVATE_ROUTES } from "routes/routes";
import { IUser } from "types/User.types";
import { IVendor } from "types/Vendor.types";
import { Book, Briefcase, ClipboardList, Coins, Footprints, Leaf, LineChart, ListTodo, Plane, Radar, Search, Settings, User, UserCog, Users, ZoomIn } from "lucide-react";

import { filterSections } from "./filterSections";

export interface MenuItem {
  id: string;
  children: string;
  icon: React.ReactNode;
  type?: string;
  closeOnSelect?: boolean;
  onClick: () => void;
}

export interface MenuSection {
  heading: string;
  id: string;
  items: (MenuItem | false)[];
}

export const hasAdminAccess = (role?: ESystemRoles) =>
  [ESystemRoles.ADMIN, ESystemRoles.LEADER].includes(role as ESystemRoles);

export const hasJobyfineAccess = (user?: IUser) =>
  user?.jobyfineAdmin || user?.jobyfineRecruiter || user?.jobyfineRequester;

export const generateMenuStructure = (
  t: any,
  user?: IUser,
  vendor?: IVendor,
  onPageChange?: (page: string) => void,
  onNavigate?: (path: string) => void,
  onClose?: () => void
): MenuSection[] => [
  {
    heading: t("Atajos"),
    id: "manager",
    items: [
      {
        id: "search-users",
        children: t("Buscar usuarios"),
        icon: <Search className="h-4 w-4 text-gray-600" />,
        closeOnSelect: false,
        onClick: () => onPageChange?.("users")
      },
      hasAdminAccess(user?.systemRole) && {
        id: "users",
        children: t("Ir a usuarios"),
        icon: <User className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.userMaintainer.path);
          onClose?.();
        }
      },
      hasAdminAccess(user?.systemRole) && {
        id: "evaluations",
        children: t("Ir a evaluaciones"),
        icon: <Book className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.appreciationList.path);
          onClose?.();
        }
      },
      hasAdminAccess(user?.systemRole) && {
        id: "areas",
        children: t("Ir a areas"),
        icon: <Users className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.areaMaintainer.path);
          onClose?.();
        }
      },
      hasAdminAccess(user?.systemRole) && {
        id: "skills",
        children: t("Ir a habilidades"),
        icon: <Settings className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.dimensionMaintainer.path);
          onClose?.();
        }
      },
      hasAdminAccess(user?.systemRole) && {
        id: "roles",
        children: t("Ir a cargos"),
        icon: <Footprints className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.roleMaintainer.path);
          onClose?.();
        }
      },
      hasAdminAccess(user?.systemRole) && {
        id: "teams",
        children: t("Ir a equipos"),
        icon: <UserCog className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.teamsLeader.path);
          onClose?.();
        }
      },
      user?.systemRole === ESystemRoles.ADMIN && {
        id: "radar",
        children: t("Ir a radar"),
        icon: <Radar className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.radar.path);
          onClose?.();
        }
      },
      vendor?.enableCoins && {
        id: "marketplace",
        children: t("Ir a marketplace"),
        icon: <Coins className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.myAppreciations.path);
          onClose?.();
        }
      },
      vendor?.enableActionPlans && {
        id: "action-plan",
        children: t("Ir a plan de acción"),
        icon: <Plane className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.actionPlans.path);
          onClose?.();
        }
      },
      {
        id: "my-evaluations",
        children: t("Ir a mis evaluaciones"),
        icon: <Leaf className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.myAppreciations.path);
          onClose?.();
        }
      },
      vendor?.enableActionPlans && {
        id: "my-action-plans",
        children: t("Ir a mis planes de acción"),
        icon: <Plane className="h-4 w-4 text-gray-600" />,
        type: "button",
        closeOnSelect: true,
        onClick: () => {
          onNavigate?.(PRIVATE_ROUTES.userActionPlans.path);
          onClose?.();
        }
      },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine",
          children: t("Ir a Jobyfine"),
          icon: <Users className="h-4 w-4 text-gray-600" />,
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineApplicants.path);
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-offers",
          children: t("Ir a Ofertas Jobyfine"),
          icon: <Briefcase className="h-4 w-4 text-gray-600" />,
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineOffers.path);
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-applicants",
          children: t("Ir a Aplicantes Jobyfine"),
          icon: <Users className="h-4 w-4 text-gray-600" />,
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineApplicants.path);
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-process",
          children: t("Ir a Procesos Jobyfine"),
          icon: <ListTodo className="h-4 w-4 text-gray-600" />,
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineProcess.path);
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-finder",
          children: t("Ir a Buscador Jobyfine"),
          icon: (
            <ZoomIn className="h-4 w-4 text-gray-600" />
          ),
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.("/jobyfine/finder");
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-dashboard",
          children: t("Ir a Dashboard Jobyfine"),
          icon: <LineChart className="h-4 w-4 text-gray-600" />,
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineDashboard.path);
            onClose?.();
          }
        },
      vendor?.enableJobyfine &&
        hasJobyfineAccess(user) && {
          id: "jobyfine-requests",
          children: t("Ir a Solicitudes Jobyfine"),
          icon: (
            <ClipboardList className="h-4 w-4 text-gray-600" />
          ),
          type: "button",
          closeOnSelect: true,
          onClick: () => {
            onNavigate?.(PRIVATE_ROUTES.jobyfineRequest.path);
            onClose?.();
          }
        }
    ].filter(Boolean) as MenuItem[]
  }
];

const CommandPallete = () => {
  const { user, commandPaletteOpen, toggleCommandPalette, setCommandPaletteOpen } =
    useUserStoreContext((store) => ({
      user: store.user,
      commandPaletteOpen: store.commandPaletteOpen,
      toggleCommandPalette: store.toggleCommandPalette,
      setCommandPaletteOpen: store.setCommandPaletteOpen
    }));

  const { t } = useTranslation("common");
  const router = useRouter();
  const { vendor } = useVendor();

  const [page, setPage] = useState<"root" | "users">("root");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);

  // La paleta no aplica en los hosts de creación de sitios.
  const [disabled] = useState(
    () =>
      typeof window !== "undefined" &&
      SITE_CREATION_HOSTS.includes(window.location.host)
  );

  const [getActiveUsers] = useLazyQuery<IVendorUserListResponse>(
    gqlUser.queries.GET_VENDOR_USER_LIST_LIGHT,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ vendorUserList }) => {
        setLoading(false);
        setUsers(vendorUserList ?? []);
      },
      onError: () => {
        setLoading(false);
        setUsers([]);
      }
    }
  );

  const closePalette = useCallback(() => {
    setCommandPaletteOpen(false);
  }, [setCommandPaletteOpen]);

  const handleNavigate = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router]
  );

  const handlePageChange = useCallback((nextPage: string) => {
    setPage(nextPage as "root" | "users");
    setSearch("");
    setLoading(false);
    setUsers([]);
  }, []);

  const isUsersPage = page === "users";

  const rootSections: CommandPaletteV2Section[] = useMemo(() => {
    if (!user || !vendor) return [];
    const structure = generateMenuStructure(
      t,
      user,
      vendor,
      handlePageChange,
      handleNavigate,
      closePalette
    );
    return filterSections(structure, search).map((section) => ({
      id: section.id,
      heading: section.heading,
      items: section.items.map((item) => ({
        id: item.id,
        closeOnSelect: item.closeOnSelect,
        onSelect: item.onClick,
        label: (
          <div className="flex items-center">
            {item.icon && (
              <span className="mr-3">
                {item.icon}
              </span>
            )}
            {item.children}
          </div>
        )
      }))
    }));
  }, [user, vendor, t, search, handlePageChange, handleNavigate, closePalette]);

  const userSections: CommandPaletteV2Section[] = useMemo(() => {
    if (!isUsersPage || loading) return [];
    return [
      {
        id: "users-results",
        items: users.map((resultUser) => ({
          id: resultUser.id,
          onSelect: () => {
            router.push(`/profile/${resultUser.id}?vendorId=${vendor?.id}`);
            setSearch("");
            setPage("root");
          },
          label: (
            <div className="flex items-center">
              <UserAvatar {...{ user: resultUser, showName: false, size: 40 }} />
              <div className="align-center ml-3 flex flex-col justify-start">
                <span className="capitalize">
                  {resultUser.name} {resultUser.lastname}
                </span>
                <p className="block text-xs capitalize text-gray-800 dark:text-gray-300">
                  {resultUser.role?.name || "-"}
                </p>
                <p className="block text-xs lowercase text-gray-600 dark:text-gray-200">
                  {resultUser.email}
                </p>
              </div>
            </div>
          )
        }))
      }
    ];
  }, [isUsersPage, loading, users, router, vendor?.id]);

  // Busca usuarios con debounce sobre el input real; limpiar el timeout
  // cancela búsquedas pendientes al salir de la página o seguir tecleando.
  useEffect(() => {
    if (!isUsersPage) return;
    if (search.length <= 1) {
      setLoading(false);
      setUsers([]);
      return;
    }
    setLoading(true);
    const debounceId = window.setTimeout(() => {
      getActiveUsers({
        variables: {
          vendorUserListInput: {
            searchText: search
          }
        }
      });
    }, 1000);
    return () => window.clearTimeout(debounceId);
  }, [search, isUsersPage, getActiveUsers]);

  // Reset al cerrar.
  useEffect(() => {
    if (!commandPaletteOpen) {
      setSearch("");
      setPage("root");
      setUsers([]);
      setLoading(false);
    }
  }, [commandPaletteOpen]);

  // Atajo Cmd+K (mac) / Ctrl+K.
  useEffect(() => {
    if (disabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggleCommandPalette();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [disabled, toggleCommandPalette]);

  if (disabled || !user) return null;

  return (
    <CommandPaletteV2
      open={commandPaletteOpen}
      onClose={closePalette}
      search={search}
      onSearchChange={setSearch}
      sections={isUsersPage ? userSections : rootSections}
      placeholder={
        isUsersPage ? t("Buscar usuarios") : t("Buscar o saltar a...")
      }
      loading={isUsersPage && loading}
      loadingLabel={t("Buscando entre los colaboradores")}
      emptyState={t("No se encontraron resultados")}
      onEscape={() => {
        if (isUsersPage) {
          handlePageChange("root");
        } else {
          closePalette();
        }
      }}
      footer={
        <div className="flex items-center justify-between text-xs">
          <span>Groowcity Commands</span>
          <span className="hidden items-center gap-1.5 sm:flex">
            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 text-[10px] dark:border-zinc-700 dark:bg-zinc-800">
              ↑↓
            </kbd>
            {t("navegar")}
            <kbd className="rounded border border-slate-200 bg-slate-50 px-1 text-[10px] dark:border-zinc-700 dark:bg-zinc-800">
              ↵
            </kbd>
            {t("seleccionar")}
          </span>
          <span>{APP_VERSION}</span>
        </div>
      }
    />
  );
};

export default CommandPallete;
